/* eslint-disable max-lines-per-function */
import { ReactComponent as DownloadIcon } from '@vkontakte/icons/src/svg/16/download_outline_16.svg';
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SharingNewDropdown } from 'reactApp/components/SharingNewBiz/Dropdown/SharingNewDropdown';
import { DOWNLOAD_FILE_OPTIONS, QA_VALUE, TOOLTIP_OFFSET } from 'reactApp/components/SharingNewBiz/SharingNew.constants';
import { DownloadAccess } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import styles from 'reactApp/components/SharingNewBiz/Weblink/SharingNewWeblink.css';
import { useOutsideClick } from 'reactApp/hooks/useOutsideClick';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { updateWeblinkCountDownloadsRequest } from 'reactApp/modules/modifying/modifying.actions';
import { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';

const DEFAULT_LIMITED_DOWNLOADS_COUNT = 10;

interface Props {
    item?: PublishItem;
    isDownloadable?: boolean;
}

export const DownloadsCountDropdown: React.FC<Props> = ({ item, isDownloadable }) => {
    const countRef = useRef<HTMLDivElement>(null);
    const [isInputActive, setIsInputActive] = useState(false);
    const [downloadCountLeft, setDownloadCountLeft] = useState<undefined | number>(undefined);
    const [downloadCountTotal, setDownloadCountTotal] = useState<undefined | number>(undefined);
    const dispatch = useDispatch();
    const isPhone = EnvironmentSelectors.isPhone();
    const checked = downloadCountLeft !== undefined && downloadCountTotal !== undefined ? DownloadAccess.Limited : DownloadAccess.Unlimited;
    useOutsideClick(countRef, () => setIsInputActive(false));

    const handleCountWrapperClick = () => setIsInputActive(true);

    const handleBlur = () => setIsInputActive(false);

    const updateCountDownloads = useCallback(
        (count_downloads?: number) => {
            if (item && count_downloads !== undefined) {
                dispatch(updateWeblinkCountDownloadsRequest({ item, count_downloads }));
            }
        },
        [dispatch, item]
    );

    const onClickItem = useCallback(
        (id: DownloadAccess) => {
            if (id === DownloadAccess.Limited) {
                return updateCountDownloads(DEFAULT_LIMITED_DOWNLOADS_COUNT);
            }

            updateCountDownloads(0);
        },
        [updateCountDownloads]
    );

    const onDownloadCountTotalChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            updateCountDownloads(+e.target.value);
        },
        [updateCountDownloads]
    );

    const renderDownloadCountDropdown = useCallback(
        (onClick: void): ReactElement => {
            const list = Object.values(DOWNLOAD_FILE_OPTIONS).map((item) => ({
                id: item.id,
                text: item.text,
                check: item.id === checked,
                qaValue: item.id,
            }));

            return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
        },
        [checked]
    );

    useEffect(() => {
        setDownloadCountLeft(item?.count_downloads_left);
    }, [item?.count_downloads_left]);

    useEffect(() => {
        setDownloadCountTotal(item?.count_downloads_total);
    }, [item?.count_downloads_total]);

    const renderDownloadsCount = useCallback((): ReactElement => {
        const item = DOWNLOAD_FILE_OPTIONS[checked];

        return (
            <div className={styles.rightsItem} data-qa-item={QA_VALUE.downloadItem}>
                <span className={styles.icon}>
                    <DownloadIcon />
                </span>
                <span className={styles.rightsTitle}>Количество скачиваний</span>
                {isDownloadable && (
                    <div className={styles.dropdown}>
                        <Dropdown
                            withinDialog
                            theme="octavius"
                            qaValue={item?.id}
                            as={SharingNewDropdown}
                            value={item.text}
                            content={renderDownloadCountDropdown}
                            onClickItem={onClickItem}
                            placement={tooltipPlacements.BOTTOM_LEFT}
                            qaValueContent={QA_VALUE.accessDropdownContent}
                            tooltipOffset={TOOLTIP_OFFSET}
                            animatedTooltip
                            description="Количество скачиваний"
                            tooltipClassName={isPhone ? styles.tooltip : undefined}
                            isPhone={isPhone}
                        />
                    </div>
                )}

                {!isDownloadable && <span className={styles.not_downloadable}>Без ограничений</span>}

                {isDownloadable && checked === DownloadAccess.Limited && (
                    <div ref={countRef}>
                        {isInputActive ? (
                            <input
                                className={styles.input_count}
                                type="text"
                                value={downloadCountTotal}
                                onChange={onDownloadCountTotalChange}
                                onBlur={handleBlur}
                                autoFocus
                            />
                        ) : (
                            <div className={styles.count_wrapper} onClick={handleCountWrapperClick}>
                                {downloadCountLeft} из {downloadCountTotal}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }, [
        checked,
        isDownloadable,
        isPhone,
        renderDownloadCountDropdown,
        onClickItem,
        isInputActive,
        downloadCountTotal,
        onDownloadCountTotalChange,
        downloadCountLeft,
    ]);

    return renderDownloadsCount();
};
